import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route } from '@angular/router';
import { authRoutes } from '@examdojo/auth';
import { SyllabusService } from '@examdojo/category/v2';
import { NotFoundComponent } from '@examdojo/core/not-found';
import { PayoutRatesService, PayoutRequestService } from '@examdojo/payments';
import { QuestionEntityStore } from '@examdojo/question';
import { ReferenceQuestionStore } from '@examdojo/reference-question';
import { mapToVoid } from '@examdojo/rxjs';
import { StudyNotesEditorComponent } from '@examdojo/study-notes';
import { ProfileComponent } from '@examdojo/user';
import { catchError, EMPTY, first } from 'rxjs';
import { ContentAnalyticsAgGridComponent } from './analytics/content-analytics-ag-grid/content-analytics-ag-grid.component';
import { UserAnalyticsAgGridComponent } from './analytics/user-analytics-ag-grid/user-analytics-ag-grid.component';
import { AuthGuard } from './auth/auth.guard';
import { CmsUrlParts, SYLLABUS_SLUG_PARAM } from './cms/cms.model';
import { PreviewFromJsonComponent } from './cms/preview-from-json/preview-from-json.component';
import { QuestionEditorContainerComponent } from './cms/question-editor-container/question-editor-container.component';
import { QuestionListContainerComponent } from './cms/question-list-container/question-list-container.component';
import { CmsReferenceQuestionsContainerComponent } from './cms/reference-questions-container/cms-reference-questions-container.component';
import { syllabusesResolver } from './cms/syllabus/syllabus.resolver';
import { SYLLABUS_ROUTES } from './cms/syllabus/syllabus.routes';
import {
  AdminContainerComponent,
  adminsUsersListResolver,
  AdminUserDetailContainerComponent,
  AdminUserStore,
} from './cms/user-management';
import { GraphComponent } from './graph';
import { AuthenticatedLayoutComponent } from './layout';
import { NoAccessComponent } from './no-access/no-access.component';
import { OntologyCanvasComponent } from './ontology-canvas/ontology-canvas.component';
import { OverviewComponent } from './overview';
import { PaymentRequestsComponent } from './payment-requests/payment-requests.component';
import { PlaygroundComponent } from './playground';
import { QuadraticFunctionsComponent } from './playground/quadratic-functions';
import { SolverDemoComponent } from './playground/solver-demo';
import { ReferenceQuestionListComponent } from './reference-question/list/reference-question-list.component';
import { legacyQuestionRedirectGuard } from './routing/legacy-question-redirect.guard';
import { legacyReferenceQuestionRedirectGuard } from './routing/legacy-reference-question-redirect.guard';
import { legacySourceRedirectGuard } from './routing/legacy-source-redirect.guard';
import { syllabusRedirectGuard } from './routing/syllabus-redirect.guard';
import { ShellComponent } from './shell/shell.component';
import { SOURCE_ROUTES } from './source/source.routes';
import { SuperLatexDemoComponent } from './super-latex-demo/super-latex-demo.component';
import { getUserRoleGuard } from './user/user-role/user-role.guard';
import { userRoleResolver } from './user/user-role/user-role.resolver';

export const appRoutes: Route[] = [
  ...authRoutes,
  {
    path: '',
    component: AuthenticatedLayoutComponent,
    canActivate: [AuthGuard],
    resolve: {
      userRole: userRoleResolver,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: CmsUrlParts.Overview,
      },
      {
        path: CmsUrlParts.NoAccess,
        component: NoAccessComponent,
      },
      {
        path: CmsUrlParts.Overview,
        canActivate: [getUserRoleGuard('ADMIN', CmsUrlParts.NoAccess)],
        component: OverviewComponent,
        resolve: {
          syllabuses: () => {
            const syllabusService = inject(SyllabusService);
            return syllabusService.fetchSyllabuses().pipe(
              first(),
              mapToVoid(),
              // error initially handled and rethrown by the service.
              catchError(() => EMPTY),
            );
          },
        },
      },
      {
        path: CmsUrlParts.ContentAnalytics,
        canActivate: [getUserRoleGuard('ADMIN', CmsUrlParts.NoAccess)],
        component: ContentAnalyticsAgGridComponent,
      },
      {
        path: CmsUrlParts.UserAnalytics,
        canActivate: [getUserRoleGuard('ADMIN', CmsUrlParts.NoAccess)],
        component: UserAnalyticsAgGridComponent,
      },
      {
        path: CmsUrlParts.PreviewFromJson,
        component: PreviewFromJsonComponent,
      },
      {
        path: CmsUrlParts.Playground,
        component: PlaygroundComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: CmsUrlParts.QuadraticFunctions,
          },
          {
            path: CmsUrlParts.QuadraticFunctions,
            component: QuadraticFunctionsComponent,
          },
          {
            path: CmsUrlParts.SuperLatex,
            component: SuperLatexDemoComponent,
          },
          {
            path: CmsUrlParts.Solver,
            component: SolverDemoComponent,
          },
          {
            path: CmsUrlParts.Graph,
            component: GraphComponent,
          },
          {
            path: CmsUrlParts.StudyNotesEditor,
            component: StudyNotesEditorComponent,
          },
        ],
      },
      {
        path: CmsUrlParts.Ontology,
        component: OntologyCanvasComponent,
      },
      {
        path: CmsUrlParts.PaymentRequests,
        canActivate: [getUserRoleGuard('ADMIN', CmsUrlParts.NoAccess)],
        resolve: {
          paymentRequests: paymentRequestsResolver,
        },
        component: PaymentRequestsComponent,
      },
      {
        path: CmsUrlParts.Admin,
        canActivate: [getUserRoleGuard('ADMIN', CmsUrlParts.NoAccess)],
        resolve: {
          adminsUsersListResolver: adminsUsersListResolver,
          payoutRates: paymentRatesResolver,
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: AdminContainerComponent,
          },
          {
            canActivate: [updateAdminUserActiveEntityOnParamChange],
            path: ':id',
            component: AdminUserDetailContainerComponent,
          },
        ],
      },
      {
        path: CmsUrlParts.Profile,
        component: ProfileComponent,
      },
      // Old routes are useful for debugging when we don't have to write the syllabus based routes by hand.
      // They are simple redirects to the new syllabus based routes.
      {
        path: CmsUrlParts.Questions,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: CmsUrlParts.Overview,
          },
          {
            path: ':id',
            canActivate: [legacyQuestionRedirectGuard],
            component: NotFoundComponent,
          },
        ],
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: CmsUrlParts.Overview,
      },
      {
        path: CmsUrlParts.ReferenceQuestions,
        children: [
          {
            path: ':id',
            canActivate: [legacyReferenceQuestionRedirectGuard],
            component: NotFoundComponent,
          },
        ],
      },
      {
        path: CmsUrlParts.Sources,
        children: [
          {
            path: ':id',
            canActivate: [legacySourceRedirectGuard],
            component: NotFoundComponent,
          },
        ],
      },

      // Syllabus-based routes
      {
        path: CmsUrlParts.Syllabus,
        canActivate: [getUserRoleGuard('ADMIN', CmsUrlParts.NoAccess)],
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [syllabusRedirectGuard],
            component: NotFoundComponent, // This won't be reached due to the guard
          },
          {
            path: `:${SYLLABUS_SLUG_PARAM}`,
            component: ShellComponent,
            resolve: {
              syllabuses: syllabusesResolver,
            },
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: CmsUrlParts.Sources,
              },
              {
                path: CmsUrlParts.Questions,
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    component: QuestionListContainerComponent,
                  },
                  {
                    canActivate: [updateQuestionActiveEntityOnParamChange],
                    path: ':id',
                    component: QuestionEditorContainerComponent,
                  },
                ],
              },
              {
                path: CmsUrlParts.ReferenceQuestions,
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    component: ReferenceQuestionListComponent,
                  },
                  {
                    canActivate: [updateReferenceQuestionActiveEntityOnParamChange],
                    path: ':id',
                    component: CmsReferenceQuestionsContainerComponent,
                  },
                ],
              },
              ...SOURCE_ROUTES,
              ...SYLLABUS_ROUTES,
            ],
          },
        ],
      },
    ],
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];

export function updateReferenceQuestionActiveEntityOnParamChange(route: ActivatedRouteSnapshot): boolean {
  inject(ReferenceQuestionStore).setActive(route.params['id']);
  return true;
}

export function updateQuestionActiveEntityOnParamChange(route: ActivatedRouteSnapshot): boolean {
  inject(QuestionEntityStore).setActive(route.params['id']);
  return true;
}

export function updateAdminUserActiveEntityOnParamChange(route: ActivatedRouteSnapshot): boolean {
  inject(AdminUserStore).setActive(route.params['id']);
  return true;
}

export function paymentRequestsResolver() {
  const paymentRequestService = inject(PayoutRequestService);

  return paymentRequestService.loadPayoutRequests().pipe(
    first(),
    mapToVoid(),
    catchError(() => EMPTY),
  );
}

export function paymentRatesResolver() {
  const payoutRatesService = inject(PayoutRatesService);

  return payoutRatesService.fetchAll().pipe(
    first(),
    mapToVoid(),
    catchError(() => EMPTY),
  );
}
